import React, { useEffect, useState, useCallback } from 'react';
import { formatCurrency, formatDate } from '../utils/helpers';
import { BookOpen, Download, Filter, ChevronLeft, ChevronRight } from 'lucide-react';
import toast from 'react-hot-toast';

const API_URL = window._env_?.REACT_APP_API_URL || process.env.REACT_APP_API_URL || '';

const ENTRY_TYPE_LABELS = {
  PAYMENT_RECEIVED:           { label: 'Payment Received',           color: '#16A34A', bg: '#F0FDF4' },
  UNBILLED_REVENUE_RECOGNISED:{ label: 'Unbilled Revenue Recognised', color: '#2563EB', bg: '#EFF6FF' },
  UNBILLED_REVENUE_CONVERTED: { label: 'Unbilled → Receivable',      color: '#7C3AED', bg: '#F5F3FF' },
  REVENUE_RECOGNISED:         { label: 'Revenue Recognised',         color: '#D97706', bg: '#FFFBEB' },
  INVOICE_RAISED:             { label: 'Invoice Raised',             color: '#0891B2', bg: '#ECFEFF' },
  REVERSAL:                   { label: 'Reversal',                   color: '#DC2626', bg: '#FEF2F2' },
};

const PAGE_SIZE = 50;

export default function AccountLedger() {
  const [entries, setEntries]     = useState([]);
  const [total, setTotal]         = useState(0);
  const [page, setPage]           = useState(0);
  const [loading, setLoading]     = useState(true);
  const [filters, setFilters]     = useState({
    from_date: '', to_date: '', entry_type: '', account: '', show_all: false,
  });
  const [summary, setSummary]     = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const baseFilters = {
        status: filters.show_all ? 'ALL' : 'POSTED',
        ...(filters.from_date  && { from_date:   filters.from_date }),
        ...(filters.to_date    && { to_date:     filters.to_date }),
        ...(filters.entry_type && { entry_type:  filters.entry_type }),
        ...(filters.account    && { account:     filters.account }),
      };
      const params = new URLSearchParams({ ...baseFilters, limit: PAGE_SIZE, offset: page * PAGE_SIZE });
      const summaryParams = new URLSearchParams(baseFilters);

      const [entriesRes, summaryRes] = await Promise.all([
        fetch(`${API_URL}/api/ledger?${params}`,         { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/ledger/summary?${summaryParams}`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      const eData = await entriesRes.json();
      const sData = await summaryRes.json();
      setEntries(eData.entries || []);
      setTotal(eData.total || 0);
      setSummary(sData.summary || null);
    } catch (err) {
      toast.error('Failed to load ledger');
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => { load(); }, [load]);

  const handleFilterChange = (key, val) => {
    setFilters(f => ({ ...f, [key]: val }));
    setPage(0);
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams({
        status: 'POSTED', export: 'csv', limit: 10000,
        ...(filters.from_date  && { from_date:  filters.from_date }),
        ...(filters.to_date    && { to_date:    filters.to_date }),
        ...(filters.entry_type && { entry_type: filters.entry_type }),
      });
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/ledger/export?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `ledger_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Export failed'); }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-xl font-semibold">Account Ledger</h2>
          <p className="text-sm mt-0.5" style={{ color: 'var(--bt-gray-500)' }}>
            All posted double-entry journal entries. Future ERP sync point.
          </p>
        </div>
        <button onClick={handleExport}
          className="flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg font-medium border"
          style={{ background: '#fff', color: 'var(--bt-gray-600)', borderColor: 'var(--bt-gray-200)' }}>
          <Download size={14}/> Export CSV
        </button>
      </div>

      {/* Summary widgets */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Total Entries',       value: summary.total_entries,   color: 'var(--bt-orange)' },
            { label: 'Total Debit',         value: formatCurrency(summary.total_debit),   color: '#DC2626', raw: true },
            { label: 'Total Credit',        value: formatCurrency(summary.total_credit),  color: '#16A34A', raw: true },
            { label: 'Revenue Recognised',  value: formatCurrency(summary.revenue_recognised), color: '#D97706', raw: true },
          ].map(w => (
            <div key={w.label} className="bt-card p-4">
              <div className="text-xs font-semibold uppercase tracking-wide mb-1"
                style={{ color: 'var(--bt-gray-500)' }}>{w.label}</div>
              <div className="text-lg font-bold" style={{ color: w.color }}>
                {w.raw ? w.value : w.value?.toLocaleString()}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="bt-card p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter size={14} style={{ color: 'var(--bt-gray-500)' }}/>
          <span className="text-sm font-medium" style={{ color: 'var(--bt-gray-600)' }}>Filters</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className="bt-label">From Date</label>
            <input type="date" className="bt-input"
              value={filters.from_date}
              onChange={e => handleFilterChange('from_date', e.target.value)}/>
          </div>
          <div>
            <label className="bt-label">To Date</label>
            <input type="date" className="bt-input"
              value={filters.to_date}
              onChange={e => handleFilterChange('to_date', e.target.value)}/>
          </div>
          <div>
            <label className="bt-label">Entry Type</label>
            <select className="bt-select" value={filters.entry_type}
              onChange={e => handleFilterChange('entry_type', e.target.value)}>
              <option value="">All Types</option>
              {Object.entries(ENTRY_TYPE_LABELS).map(([v, { label }]) => (
                <option key={v} value={v}>{label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="bt-label">Account (Dr or Cr)</label>
            <input className="bt-input" placeholder="e.g. Revenue A/c"
              value={filters.account}
              onChange={e => handleFilterChange('account', e.target.value)}/>
          </div>
          <div className="flex items-end pb-0.5">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={filters.show_all}
                onChange={e => handleFilterChange('show_all', e.target.checked)}
                className="rounded"/>
              <div>
                <div className="text-sm font-medium" style={{ color: 'var(--bt-gray-700)' }}>Include Draft entries</div>
                <div className="text-xs" style={{ color: 'var(--bt-gray-400)' }}>Show pending Accounts Officer approval</div>
              </div>
            </label>
          </div>
        </div>
        {(filters.from_date || filters.to_date || filters.entry_type || filters.account) && (
          <button onClick={() => { setFilters({ from_date:'', to_date:'', entry_type:'', account:'' }); setPage(0); }}
            className="mt-2 text-xs underline" style={{ color: 'var(--bt-gray-400)', background:'none', border:'none', cursor:'pointer' }}>
            Clear filters
          </button>
        )}
      </div>

      {/* Ledger table */}
      <div className="bt-card overflow-hidden">
        <div className="bt-card-header">
          <div className="flex items-center gap-2">
            <BookOpen size={16} style={{ color: 'var(--bt-orange)' }}/>
            <span className="bt-card-title">
              Posted Entries
              {total > 0 && <span className="ml-2 text-sm font-normal" style={{ color: 'var(--bt-gray-400)' }}>
                ({page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total})
              </span>}
            </span>
          </div>
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
                style={{ background:'none', border:'none', cursor: page===0?'default':'pointer', opacity: page===0?0.3:1 }}>
                <ChevronLeft size={16}/>
              </button>
              <span className="text-xs" style={{ color: 'var(--bt-gray-500)' }}>
                {page + 1} / {totalPages}
              </span>
              <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}
                style={{ background:'none', border:'none', cursor: page>=totalPages-1?'default':'pointer', opacity: page>=totalPages-1?0.3:1 }}>
                <ChevronRight size={16}/>
              </button>
            </div>
          )}
        </div>

        {loading ? (
          <div className="p-8 text-center" style={{ color: 'var(--bt-gray-400)' }}>
            Loading ledger entries…
          </div>
        ) : entries.length === 0 ? (
          <div className="p-12 text-center">
            <BookOpen size={36} className="mx-auto mb-3" style={{ color: 'var(--bt-gray-300)' }}/>
            <p className="font-medium" style={{ color: 'var(--bt-gray-600)' }}>No posted entries found</p>
            <p className="text-sm mt-1" style={{ color: 'var(--bt-gray-400)' }}>
              Entries appear here once approved by Accounts Officer.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" style={{ tableLayout: 'fixed', minWidth: 900 }}>
              <colgroup>
                <col style={{ width: 96 }}/>   {/* Date */}
                <col style={{ width: 140 }}/>  {/* Entry Type */}
                <col style={{ width: 175 }}/>  {/* Dr Account */}
                <col style={{ width: 175 }}/>  {/* Cr Account */}
                <col style={{ width: 110 }}/>  {/* Amount */}
                <col/>                          {/* Narration */}
                <col style={{ width: 80 }}/>   {/* Status */}
                <col style={{ width: 100 }}/>  {/* Reference */}
              </colgroup>
              <thead>
                <tr>
                  {['Date','Entry Type','Debit Account','Credit Account','Amount','Narration','Status','Reference'].map(h => (
                    <th key={h} className="table-th">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {entries.map(je => {
                  const typeInfo = ENTRY_TYPE_LABELS[je.entry_type] || { label: je.entry_type?.replace(/_/g,' '), color: 'var(--bt-gray-600)', bg: 'var(--bt-gray-100)' };
                  return (
                    <tr key={je.journal_id}
                      style={{ background: je.is_reversal ? '#FEF2F2' : undefined }}>
                      <td className="table-td text-sm">{formatDate(je.entry_date)}</td>
                      <td className="table-td">
                        <span className="text-xs font-medium px-2 py-0.5 rounded-full"
                          style={{ background: typeInfo.bg, color: typeInfo.color }}>
                          {typeInfo.label}
                        </span>
                        {je.is_reversal && (
                          <span className="ml-1 text-xs" style={{ color: '#DC2626' }}>(R)</span>
                        )}
                      </td>
                      <td className="table-td text-sm font-medium">{je.debit_account}</td>
                      <td className="table-td text-sm" style={{ color: 'var(--bt-gray-500)' }}>{je.credit_account}</td>
                      <td className="table-td font-semibold">{formatCurrency(je.amount, je.currency)}</td>
                      <td className="table-td text-xs" style={{ color: 'var(--bt-gray-500)' }}>{je.narration}</td>
                      <td className="table-td">
                        {je.status === 'POSTED'
                          ? <span className="bt-badge bt-badge-green" style={{ fontSize: 10 }}>Posted</span>
                          : <span className="bt-badge bt-badge-amber" style={{ fontSize: 10 }}>Draft</span>
                        }
                      </td>
                      <td className="table-td text-xs font-mono" style={{ color: 'var(--bt-gray-400)' }}>
                        {je.invoice_number || je.po_number || je.journal_id?.slice(0,8)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
